import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays } from "date-fns";
import React from "react";
import { Aircraft, Tenant, TenantType } from "../../../types";
import { SCHEDULE_MAX_DATE, SCHEDULE_MIN_DATE } from "../../../utils/dateConstants";
import { isInfinityDate, INFINITY_DATE_STRING } from "../../../utils/dateHelpers";
import { SelectTailNumber } from "../../../widgets/SelectTailNumber";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";

interface CreateTripDialogPresenterProps {
  // Dialog state
  open: boolean;
  onClose: () => void;

  // Form data
  formData: {
    tailNumber: string;
    ownerManager: string;
    tenantType: string;
    selectedAircraft: Aircraft | null;
    selectedTenant: Tenant | null;
    arrivalDate: Date | null;
    departureDate: Date | string | null; // Can be Date or "infinity" string
    notes: string;
  };

  // Form handlers
  setFormData: (data: any) => void;
  handleTenantChange: (tenant: Tenant) => void;
  handleTailNumberChange: (tailNumber: string) => void;
  handleSubmit: () => void;

  // Validation
  validationErrors: {
    tailNumber?: string;
    aircraft?: string;
    arrivalDate?: string;
    departureDate?: string;
  };
  isFormValid: boolean;

  // Loading states
  loading: boolean;
  checkingConflicts: boolean;

  // Errors and warnings
  error: string | null;
  setError: (error: string | null) => void;
  conflictWarning: string | null;
  setConflictWarning: (warning: string | null) => void;

  // Available tenants
  tenants: Tenant[];

  // Location info
  currentLocationId?: string;
  currentLocationType?: "hangar" | "ramp";

  // Simple mode
  simpleMode?: boolean;
  existingTenant?: any; // TripWithTenant type
}

export const CreateTripDialogPresenter: React.FC<
  CreateTripDialogPresenterProps
> = ({
  open,
  onClose,
  formData,
  setFormData,
  handleTenantChange,
  handleTailNumberChange,
  handleSubmit,
  validationErrors,
  isFormValid,
  loading,
  checkingConflicts,
  error,
  setError,
  conflictWarning,
  setConflictWarning,
  tenants,
  currentLocationId,
  currentLocationType,
  simpleMode = false,
  existingTenant,
}) => {
  // Track whether "No scheduled departure" checkbox is checked
  const [noScheduledDeparture, setNoScheduledDeparture] = React.useState(false);

  // Sync checkbox state with formData.departureDate
  React.useEffect(() => {
    setNoScheduledDeparture(isInfinityDate(formData.departureDate));
  }, [formData.departureDate]);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-trip-dialog-title"
        PaperProps={{
          sx: {
            // iPad optimization
            maxWidth: { xs: "100%", sm: "500px", md: "600px" },
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: "100vh", sm: "85vh" },
            zIndex: 9600, // Higher than TripDetailsModal (9500)
          },
        }}
        sx={{
          zIndex: 9600, // Ensure backdrop is also above TripDetailsModal
        }}
      >
        <DialogTitle id="create-trip-dialog-title">
          {simpleMode && existingTenant
            ? `New Trip for ${existingTenant.tail_number}`
            : "Create New Trip"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Error display */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Conflict warning */}
            {conflictWarning && (
              <Alert
                severity="warning"
                onClose={() => setConflictWarning(null)}
                action={
                  checkingConflicts ? <CircularProgress size={20} /> : undefined
                }
              >
                {conflictWarning}
              </Alert>
            )}

            {/* Show aircraft info in simple mode, or full form fields in normal mode */}
            {simpleMode && existingTenant ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{existingTenant.tail_number}</strong>
                  {existingTenant.owner_manager &&
                    ` • ${existingTenant.owner_manager}`}
                  {existingTenant.aircraft_model &&
                    ` • ${existingTenant.aircraft_model}`}
                </Typography>
              </Alert>
            ) : (
              <>
                {/* Tail Number Selection */}
                <SelectTailNumber
                  key={open ? "dialog-open" : "dialog-closed"}
                  disableAutocomplete={false}
                  defaultValue=""
                  tenants={tenants}
                  onInputChange={handleTailNumberChange}
                  onChange={handleTenantChange}
                  warning={validationErrors.tailNumber}
                />

                <Stack direction="row" spacing={2}>
                  {/* Owner/Manager */}
                  <TextField
                    label="Owner/Manager"
                    value={formData.ownerManager}
                    onChange={(e) =>
                      setFormData({ ownerManager: e.target.value })
                    }
                    fullWidth
                    size="small"
                    placeholder="e.g., John Smith"
                  />

                  {/* Tenant Type */}
                  <TextField
                    select
                    label="Type"
                    value={formData.tenantType}
                    onChange={(e) =>
                      setFormData({ tenantType: e.target.value })
                    }
                    size="small"
                    sx={{ minWidth: 120 }}
                    SelectProps={{
                      MenuProps: {
                        sx: {
                          zIndex: 10000, // Higher than dialog (9600)
                        },
                      },
                    }}
                  >
                    <MenuItem value={TenantType.TRANSIENT}>Transient</MenuItem>
                    <MenuItem value={TenantType.BASE}>Base</MenuItem>
                  </TextField>
                </Stack>

                {/* Aircraft Selection */}
                <Stack spacing={1}>
                  <AutocompleteAircraft
                    key={formData.selectedAircraft?.id || "no-aircraft"}
                    label="Search by make and model"
                    onSelect={(aircraft) =>
                      setFormData({ selectedAircraft: aircraft })
                    }
                    defaultSelected={formData.selectedAircraft}
                  />
                  {validationErrors.aircraft && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {validationErrors.aircraft}
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {/* Arrival Date */}
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Arrival Date & Time <span style={{ color: 'red' }}>*</span>
              </Typography>
              <DateTimePicker
                label=""
                value={formData.arrivalDate}
                onChange={(newValue) => setFormData({ arrivalDate: newValue })}
                minDate={SCHEDULE_MIN_DATE}
                ampm={false}
                minutesStep={15}
                maxDate={SCHEDULE_MAX_DATE}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: "small",
                    error: !!validationErrors.arrivalDate,
                    helperText: validationErrors.arrivalDate,
                  },
                  popper: {
                    sx: {
                      zIndex: 10000, // Higher than dialog (9600)
                    },
                  },
                }}
              />
            </Stack>

            {/* Departure Date */}
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Departure Date & Time {!noScheduledDeparture && <span style={{ color: 'red' }}>*</span>}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noScheduledDeparture}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNoScheduledDeparture(checked);
                        if (checked) {
                          // Set departure to infinity
                          setFormData({ departureDate: INFINITY_DATE_STRING });
                        } else {
                          // Reset to default (2 days from arrival)
                          const defaultDeparture = formData.arrivalDate
                            ? addDays(formData.arrivalDate, 2)
                            : addDays(new Date(), 2);
                          setFormData({ departureDate: defaultDeparture });
                        }
                      }}
                      disabled={loading}
                    />
                  }
                  label="No departure"
                />
              </Stack>

              {!noScheduledDeparture ? (
                <DateTimePicker
                  label=""
                  value={formData.departureDate}
                  onChange={(newValue) => setFormData({ departureDate: newValue })}
                  minDate={
                    formData.arrivalDate && formData.arrivalDate > SCHEDULE_MIN_DATE
                      ? formData.arrivalDate
                      : SCHEDULE_MIN_DATE
                  }
                  ampm={false}
                  minutesStep={15}
                  minDateTime={formData.arrivalDate || undefined}
                  maxDate={SCHEDULE_MAX_DATE}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: "small",
                      error: !!validationErrors.departureDate,
                      helperText: validationErrors.departureDate,
                    },
                    popper: {
                      sx: {
                        zIndex: 10000, // Higher than dialog (9600)
                      },
                    },
                  }}
                />
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This aircraft will remain at the FBO indefinitely with no scheduled departure.
                </Alert>
              )}
            </Stack>

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              size="small"
              placeholder="Any special requirements or notes..."
            />

            {/* Info box - show different message for simple mode */}
            {simpleMode ? (
              currentLocationId ? (
                <Alert severity="success">
                  This trip will be automatically assigned to the current{" "}
                  {currentLocationType}.
                </Alert>
              ) : (
                <Alert severity="info">
                  The trip will be created for this aircraft and can be
                  positioned after creation.
                </Alert>
              )
            ) : (
              <>
                {!currentLocationId && (
                  <Alert severity="info">
                    The aircraft will be initially placed as "unassigned" and
                    can be positioned in a hangar or ramp area after creation.
                  </Alert>
                )}
                {currentLocationId && (
                  <Alert severity="success">
                    This trip will be automatically assigned to the current{" "}
                    {currentLocationType}.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isFormValid || !!conflictWarning}
            title={
              conflictWarning
                ? "Cannot create trip - overlaps with existing trip"
                : !isFormValid
                ? "Please fill in all required fields"
                : ""
            }
          >
            {loading ? <CircularProgress size={24} /> : "Create Trip"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
